// Type-only, so nothing about PowerSync is pulled into this package at runtime — see the note on
// the context below for why that matters.
import type { FileTypeGroup } from '@marine/services/src/powersync/queries/files/fileTypeGroups';
import { createContext, useContext } from 'react';

/**
 * One already-uploaded file, as the picker hands it back.
 *
 * The same shape the dashboard's `PickedFile` has, declared here so this package can describe what
 * it asks for without importing it. Keyed by the file's **uuid**, which is what referencing a file
 * means everywhere it is referenced: `fileables.file_uuid` to attach it, `image_uuid` to make it a
 * cover, `GET /files/{uuid}` to render it.
 */
export type PickerFile = {
  /** The file's uuid. */
  id: string;
  /**
   * The numeric backend id, carried for the Lumen forms in this package and in `@marine/boats`.
   *
   * Those mutations take integer file ids, and their fields are typed by GraphQL queries that have
   * never seen a uuid — so a file picked in this session has to be able to answer in the currency
   * they speak. `getFilesIds` is what reads it.
   *
   * Optional because the picker cannot always promise it, and nothing in the current stack needs it
   * to: it is absent for a file whose row has not synced down yet.
   *
   * @deprecated Only the Lumen forms read this. Everything else references a file by `id`.
   */
  laravel_id?: number;
  name: string;
  size: number;
  type: string;
  extension: string;
};

/** What a component wants the picker to show. */
export type PickFilesRequest = {
  title?: string;
  /** Defaults to true — most callers here attach several files at once. */
  multiple?: boolean;
  /** The type groups the caller can take. Absent means anything. */
  accept?: FileTypeGroup[];
  /** What is already chosen, so reopening shows the current selection. */
  initialFiles?: PickerFile[];
};

/**
 * Opens the picker and resolves with what was chosen, or `undefined` if it was cancelled.
 *
 * A promise rather than a rendered component, because every caller in this package wants the same
 * thing — open it, get files, write them to a field — and none of them wants to own a modal's
 * open state to do it.
 */
export type PickFiles = (request?: PickFilesRequest) => Promise<PickerFile[] | undefined>;

/**
 * The picker, supplied by the application rather than imported here.
 *
 * This package is consumed by `theboat_db` as well as the dashboard, and the two cannot share a
 * picker: the real one reads the local PowerSync tables so it works offline, and `theboat_db` is a
 * Next.js site with no PowerSync and no `@powersync/*` dependency. Importing it here would put a
 * sync engine into the module graph of a marketing site that only wants `FileIcon`.
 *
 * So the components here state what they need and the app installs something that can do it. The
 * dashboard mounts `FilePickerHost`; `theboat_db` mounts nothing, renders none of these
 * components, and stays unaware that a picker exists at all.
 */
const FilePickerContext = createContext<PickFiles | null>(null);

export const FilePickerContextProvider = FilePickerContext.Provider;

/**
 * The app's picker.
 *
 * Throws rather than returning a no-op when nothing is installed: a button that silently does
 * nothing is a bug that reaches production, and every component calling this is only ever rendered
 * inside the dashboard, which mounts the host at its root.
 */
export const useFilePicker = (): PickFiles => {
  const pickFiles = useContext(FilePickerContext);

  if (!pickFiles) {
    throw new Error('Wrap your components tree with a FilePickerHost to use the file picker.');
  }

  return pickFiles;
};
